import type { HomeLayout } from "@/features/navigation/homeLayout";
import { apiClient } from "@/lib/api/client";
import { ENDPOINTS } from "@/lib/api/config";

export interface UserStats {
  total_stashes: number;
  total_tags: number;
  total_events: number;
  total_places: number;
}

export interface UserCoordinates {
  lat: number;
  lng: number;
}

export interface UserSettings {
  notifications_enabled?: boolean;
  email_notifications?: boolean;
  show_sensitive_content?: boolean;
  theme_preference?: "light" | "dark";
  // Display preferences
  show_titles_on_thumbs?: boolean;
  default_upload_visibility?: "public" | "private";
}

export interface UserProfile {
  home_layout?: HomeLayout | null;
  user_id: string;
  email: string;
  email_verified?: boolean;
  is_admin?: boolean;
  name?: string;
  age?: number;
  birth_date?: string;
  stasht_username?: string;
  description?: string;
  instagram_handle?: string;
  avatar_url?: string;
  created_at?: string;
  profile_updated_at?: string;
  total_stashes?: number;
  phone?: string;
  home_location?: string;
  home_coordinates?: UserCoordinates;
  timezone_id?: string;
  utc_offset_minutes?: number;
  setup_complete?: boolean;
  username?: string;
  stats?: UserStats;
  settings?: UserSettings;
  onboarding_progress?: {
    last_step?: string;
    skipped_location?: boolean;
    setup_stasht_completed?: boolean;
    welcome_seen?: boolean;
    updated_at?: string;
  };
  /** @deprecated Use `is_gated` and `activation_stage` instead */
  gating_status?: "registered" | "onboarded" | "active";
  is_gated?: boolean;
  activation_stage?: "registered" | "onboarded" | "active" | "merged";
}

export interface ProfileUpdateRequest {
  home_layout?: HomeLayout | null;
  name?: string;
  email?: string;
  stasht_username?: string;
  description?: string;
  instagram_handle?: string;
  avatar_url?: string;
  phone?: string;
  username?: string;
  show_sensitive_content?: boolean;
  theme_preference?: "light" | "dark";
  notifications_enabled?: boolean;
  home_location?: string;
  home_coordinates?: UserCoordinates;
  timezone_id?: string;
  utc_offset_minutes?: number;
  // Display preferences
  show_titles_on_thumbs?: boolean;
  default_upload_visibility?: "public" | "private";
}

export interface ProfileSetupRequest {
  name?: string;
  birth_date?: string;
  home_location?: string;
  home_coordinates?: UserCoordinates;
  timezone_id?: string;
  utc_offset_minutes?: number;
  stasht_username?: string;
}

export interface UsernameCheckResponse {
  available: boolean;
  current_user?: boolean;
  reason?: string;
}

export interface EmailCheckResponse {
  available: boolean;
  reason?: string;
}

class ProfileService {
  async getProfile(): Promise<UserProfile> {
    const response = await apiClient.get<UserProfile>(ENDPOINTS.PROFILE.GET);
    return response;
  }

  async updateProfile(updates: ProfileUpdateRequest): Promise<UserProfile> {
    const response = await apiClient.put<UserProfile>(ENDPOINTS.PROFILE.UPDATE, updates);
    return response;
  }

  async checkUsernameAvailability(
    username: string,
    options: { skipAuth?: boolean } = {},
  ): Promise<UsernameCheckResponse> {
    const response = await apiClient.get<UsernameCheckResponse>(
      `${ENDPOINTS.PROFILE.CHECK_USERNAME}?username=${encodeURIComponent(username)}`,
      options.skipAuth ?? false,
    );
    return response;
  }

  async checkEmailAvailability(
    email: string,
    options: { skipAuth?: boolean } = {},
  ): Promise<EmailCheckResponse> {
    const response = await apiClient.get<EmailCheckResponse>(
      `${ENDPOINTS.PROFILE.CHECK_EMAIL}?email=${encodeURIComponent(email)}`,
      options.skipAuth ?? false,
    );
    return response;
  }

  async completeSetup(setupData: ProfileSetupRequest): Promise<UserProfile> {
    const response = await apiClient.post<{ user: UserProfile }>(ENDPOINTS.PROFILE.SETUP, setupData);
    return response.user;
  }

  async updateShowSensitiveContent(showSensitive: boolean): Promise<UserProfile> {
    return this.updateProfile({ show_sensitive_content: showSensitive });
  }

  async updateThemePreference(themePreference: "light" | "dark"): Promise<UserProfile> {
    return this.updateProfile({ theme_preference: themePreference });
  }

  async lookupUsers(query: string): Promise<Array<{ user_id: string; username?: string; name?: string; avatar_url?: string }>> {
    const response = await apiClient.get<Array<{ user_id: string; username?: string; name?: string; avatar_url?: string }>>(
      `${ENDPOINTS.PROFILE.LOOKUP_USERS}?q=${encodeURIComponent(query)}`,
    );
    return response;
  }
}

export const profileService = new ProfileService();
